// @givmo/board — every word CBG-YR6-OUTSIDE-WIDEN adds: someone who serves on a committee without
// being on the board, added to the board (design-214's frames w1–w5 and n2; the email, w6, is the
// backend's template; backend `board-committee-only-seats.md` §19–§24).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them, as built, "Back" included. The line marked APPROVED is a shipped line reused word
// for word (the seat dialog's failure); Jonathan approved every other line in CBG-YR6-COPY1's later
// part (in-chat 2026-10-17 17:42 PDT, "1 and 2, approved.";
// `scratchpad/2026-10-17-widen-copy-round/copy-review.html`). A line added or changed since is
// marked DRAFT where it sits, until its own round. Each names the frame that draws it, or "not
// drawn" for a state no frame shows.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what Board records and what the seat opens, never that the
// organization may or must make someone a member of its board, or that they were elected. A person is
// named in full; the sentences after the name say "they", as the seat's page and the removal dialog
// do. A committee's name never sits inside a sentence's grammar: it stands alone, in brackets (the
// rule CBG-FIX97's approved words follow).

/** "A and B", "A, B and C" — committee names kept whole, as the registry records them. */
const listed = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** What the board's year gives someone joining it today, each part only when it applies. */
export interface JoinerFacts {
  /** The board has adopted its new member checklist: the seat gets a copy. */
  list: boolean;
  /** The year whose questionnaire an officer may send them (open, or the latest closed), or null. */
  coiLabel: string | null;
  /** An open self-assessment's name, or null. */
  assessmentLabel: string | null;
}

export const boardJoiningWords = {
  // ── "Edit this seat" for someone who isn't on the board (frame w1) ───────────────────────────────

  /** Drawn w1: the dialog's last part, above its buttons. */
  notOnTheBoard: (name: string) => `${name} isn’t on the board.`,
  /** Drawn w1: the way to the act. */
  addToBoard: 'Add to the board…',

  // ── The act (frame w2) ───────────────────────────────────────────────────────────────────────────

  /** Drawn w2: the dialog's title once the act is chosen. */
  title: (name: string) => `Add ${name} to the board`,
  /** Drawn w2: what opens to them, said before it does. */
  sees: 'They’ll see the board’s meetings, papers, minutes and written consents, earlier ones included, as everyone serving on the board does.',
  /**
   * Drawn w2 (the list and the year; the others not drawn): what the board's year gives
   * a joiner — the board's new member checklist when it has adopted one, and the year and the open
   * self-assessment that list them as joining that day, for an officer to send. Null when none
   * applies.
   */
  joinerLine: (facts: JoinerFacts): string | null => {
    const send = [
      ...(facts.coiLabel !== null ? [`the ${facts.coiLabel} conflicts of interest form`] : []),
      ...(facts.assessmentLabel !== null ? [`the ${facts.assessmentLabel} self-assessment`] : []),
    ];
    const sendPart = send.length > 0 ? `you can send them ${listed(send)}` : null;
    // Drawn w2: the board's list by its new name (CBG-UX6-COPY), both forms; approved in the
    // wayfinding copy round (2026-10-24).
    if (facts.list && sendPart !== null)
      return `They’ll get your board’s new member checklist, and ${sendPart}.`;
    if (facts.list) return 'They’ll get your board’s new member checklist.';
    if (sendPart !== null) return `${sendPart.charAt(0).toUpperCase()}${sendPart.slice(1)}.`;
    return null;
  },
  /** Not drawn: while the board's upcoming meetings are sent as calendar invitations. */
  calendarLine: 'They’ll be sent invitations to the board’s upcoming meetings.',
  /** Drawn w2: the two facts that stay their own (D-CBG-21). */
  directorLater:
    'Whether they’re a director, and any officer title, you record afterwards in Edit this seat.',
  /** Drawn w2. */
  oneWay: 'This can’t be undone.',
  /** Drawn w2: a seat someone has joined is told by email (Q2). */
  emailed: (name: string) => `We’ll email ${name} to say so.`,
  /** Not drawn: a seat invited and not yet joined. */
  invited: 'The invitation already sent to them opens the board too.',
  /** Drawn w2: the way back to the dialog's first view. New to Board (no Board page had a "Back"
   *  before), approved with the rest in this round (the widen copy page, section 1). */
  back: 'Back',
  /** Drawn w2: the act's button. */
  confirm: 'Add to the board',
  /** Not drawn: the toast. Removed before finishing (the build's one): "We’ve
   *  emailed them to say so." after it — the act's view said so before the press, and the email
   *  leaves after the commit, where this page can't see whether it went. */
  done: 'Added to the board.',
  /** APPROVED, reused (the seat dialog's): a failure with no sentence of the backend's. */
  failed: 'We couldn’t save that. Please try again.',

  // ── The seat's history (frame w4) ────────────────────────────────────────────────────────────────

  /** Drawn w4: the one roster entry the act writes, in the shape of the approved "Added
   *  with <address> by <name>"; the backend's minute-book copy prints the same. */
  historyLine: (by: string) => `Added to the board${by}`,

  // ── Recording a board seat as former, when it serves on committees (frame n2) ───────────────────

  /** Drawn n2 (several not drawn): the memberships the removal ends
   *  (`board-committees.md` §6), named in brackets, and the way to keep the person on one. */
  leavesCommittees: (names: readonly string[]) =>
    names.length === 1
      ? `They’ll also leave their committee (${listed(names)}). To keep them on it, add them again from the committee’s page, as “Someone who isn’t on the board”.`
      : `They’ll also leave their committees (${listed(names)}). To keep them on one, add them again from that committee’s page, as “Someone who isn’t on the board”.`,
} as const;
